// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import Row from '@tcp/core/src/components/common/atoms/Row';
import Col from '@tcp/core/src/components/common/atoms/Col';
import TextBox from '@tcp/core/src/components/common/atoms/TextBox';
import Select from '../../../atoms/Select';
import CreditCardNumber from '../../../atoms/CreditCardNumber';
import { getCreditCardExpirationOptionMap } from '../../../../../utils';
import styles from '../styles/CreditCardFields.style';
import withStyles from '../../../hoc/withStyles';
import { normalizeCCExpMonthYear } from '../utils/CreditCardUtils';

export const handleCVVNumber = (value, previousValue) => {
  const re = /^[0-9\b]+$/;

  if (value === '' || re.test(value.replace(/ /g, ''))) {
    return value;
  }
  return previousValue;
};

export const handleEditCreditCardNumber = (value, previousValue) => {
  return value.startsWith('*') ? '' : handleCVVNumber(value, previousValue);
};

const CardNumberField = ({
  cardTypeImgUrl,
  cardType,
  isPLCCEnabled,
  creditFieldLabels,
  onCardFocus,
  spc,
  showIndicatorIcon,
  isExpirationRequired,
}) => {
  return (
    <Field
      placeholder={creditFieldLabels.creditCardNumber}
      name="cardNumber"
      id="cardNumber"
      type="tel"
      showIndicatorIcon={showIndicatorIcon}
      onFocus={onCardFocus}
      component={CreditCardNumber}
      dataLocator="cardNbrTxtBox"
      cardTypeImgUrl={cardTypeImgUrl}
      isPLCCEnabled={isPLCCEnabled}
      cardType={cardType}
      className="field credit-card-number"
      enableSuccessCheck={false}
      normalize={handleEditCreditCardNumber}
      spc={spc}
      isRequired
      autoComplete={!spc || isExpirationRequired ? 'on' : 'off'}
    />
  );
};

export const CreditCardFields = ({
  creditFieldLabels,
  cardTypeImgUrl,
  isExpirationRequired,
  isPLCCEnabled,
  cardType,
  className,
  cardNumbProps,
  expMonthProps,
  expYearProps,
  cvvProps,
  cvvInfo,
  syncErrors,
  showCvv,
  cardNumberWrapper,
  cardNumberInnerProps,
  onCardFocus,
  spc,
  expiredCard,
  showIndicatorIcon,
  isPLCCSection,
}) => {
  const expMonthOptionsMap = getCreditCardExpirationOptionMap().monthsMap;
  const expYearOptionsMap = getCreditCardExpirationOptionMap().yearsMap;
  return (
    <Row fullBleed className={`${className} card-fields-container`} syncErrors={syncErrors}>
      {(!expiredCard || isPLCCSection) && (
        <Col {...cardNumbProps} className="cc-num-field">
          {cardNumberWrapper ? (
            <Row fullBleed>
              <Col {...cardNumberInnerProps}>
                {CardNumberField({
                  cardTypeImgUrl,
                  cardType,
                  isPLCCEnabled,
                  creditFieldLabels,
                  onCardFocus,
                  spc,
                  showIndicatorIcon,
                  isExpirationRequired,
                })}
              </Col>
            </Row>
          ) : (
            CardNumberField({
              cardTypeImgUrl,
              cardType,
              isPLCCEnabled,
              creditFieldLabels,
              onCardFocus,
              spc,
              showIndicatorIcon,
              isExpirationRequired,
            })
          )}
        </Col>
      )}
      {isExpirationRequired && (
        <React.Fragment>
          {spc ? (
            <Col {...expMonthProps} className="exp-month-field">
              <Field
                placeholder="MM/YY"
                name="expMonthAndYear"
                id="expMonthAndYear"
                component={TextBox}
                showIndicatorIcon={showIndicatorIcon}
                dataLocator="ccExpField"
                className="field select-field"
                enableSuccessCheck={false}
                dateFormat="MM/yy"
                maxLength={5}
                ariaLabel="Expiry date"
                normalize={normalizeCCExpMonthYear}
                spc
                isRequired
              />
            </Col>
          ) : (
            <>
              <Col {...expMonthProps} className="exp-month-field">
                <Field
                  placeholder={creditFieldLabels.expMonth}
                  title={creditFieldLabels.expMonth}
                  name="expMonth"
                  id="expMonth"
                  component={Select}
                  dataLocator="expMonthDropDown"
                  options={expMonthOptionsMap}
                  className="field select-field"
                  enableSuccessCheck={false}
                />
              </Col>
              <Col {...expYearProps} className="exp-year-field">
                <Field
                  placeholder={creditFieldLabels.expYear}
                  title={creditFieldLabels.expYear}
                  name="expYear"
                  id="expYear"
                  component={Select}
                  dataLocator="expYearDropDown"
                  options={expYearOptionsMap}
                  className="field select-field"
                  enableSuccessCheck={false}
                />
              </Col>
            </>
          )}
          <Field
            name="cardTypeRadio"
            id="cardTypeRadio"
            component="input"
            type="hidden"
            spc={spc}
          />
          {showCvv && (
            <Col {...cvvProps} className="cvv-field">
              <Field
                placeholder={creditFieldLabels.cvvCode}
                name="cvvCode"
                id="cvvCode"
                type="tel"
                component={TextBox}
                dataLocator="cvvField"
                showIndicatorIcon={showIndicatorIcon}
                maxLength="4"
                enableSuccessCheck={false}
                autoComplete="off"
                spc={spc}
                isRequired
                normalize={handleCVVNumber}
                cvvInfo={cvvInfo}
              />
            </Col>
          )}
        </React.Fragment>
      )}
    </Row>
  );
};

CreditCardFields.propTypes = {
  creditFieldLabels: PropTypes.shape({}),
  onCardFocus: PropTypes.func,
  isExpirationRequired: PropTypes.bool,
  cardTypeImgUrl: PropTypes.string,
  isPLCCEnabled: PropTypes.bool,
  cardType: PropTypes.string,
  expMonthOptionsMap: PropTypes.shape([]).isRequired,
  expYearOptionsMap: PropTypes.shape([]).isRequired,
  className: PropTypes.string,
  cardNumbProps: PropTypes.shape({}).isRequired,
  expMonthProps: PropTypes.shape({}).isRequired,
  expYearProps: PropTypes.shape({}).isRequired,
  cvvProps: PropTypes.shape({}),
  cvvInfo: PropTypes.shape({}).isRequired,
  syncErrors: PropTypes.shape({}),
  showCvv: PropTypes.bool,
  cardNumberWrapper: PropTypes.bool,
  cardNumberInnerProps: PropTypes.shape({}),
  spc: PropTypes.bool,
  expiredCard: PropTypes.bool,
  showIndicatorIcon: PropTypes.bool,
};

CreditCardFields.defaultProps = {
  creditFieldLabels: {
    creditCardNumber: '',
    expMonth: '',
    expYear: '',
    cvvCode: '',
  },
  isExpirationRequired: true,
  cardTypeImgUrl: '',
  cardType: '',
  isPLCCEnabled: true,
  className: '',
  syncErrors: null,
  cvvProps: {},
  showCvv: true,
  cardNumberWrapper: false,
  cardNumberInnerProps: null,
  onCardFocus: () => {},
  spc: false,
  expiredCard: false,
  showIndicatorIcon: true,
};

CardNumberField.propTypes = {
  cardTypeImgUrl: PropTypes.string,
  isPLCCEnabled: PropTypes.bool,
  cardType: PropTypes.string,
  onCardFocus: PropTypes.func,
  creditFieldLabels: PropTypes.shape({}),
  spc: PropTypes.bool,
  isExpirationRequired: PropTypes.bool,
  showIndicatorIcon: PropTypes.bool,
};

CardNumberField.defaultProps = {
  creditFieldLabels: {
    creditCardNumber: '',
    expMonth: '',
    expYear: '',
    cvvCode: '',
  },
  cardTypeImgUrl: '',
  cardType: '',
  isPLCCEnabled: true,
  onCardFocus: () => {},
  spc: false,
  showIndicatorIcon: true,
  isExpirationRequired: false,
};

export default withStyles(CreditCardFields, styles);
export { CreditCardFields as CreditCardFieldsVanilla };
